import React, { useCallback, useEffect, useState } from 'react';
import DataTable from '../../../Elements/DataTable/DataTableFigma';
import FigmaButtonRow from '../../../Elements/FigmaButtonRow';
import GeneralEditWizzardBase from '../../../Elements/GeneralEditWizzard/GeneralEditWizzardBaseFigma';
import ErrorMessage from '../../../Elements/ErrorMessage';
import Loading from '../../../Elements/loading';
import ButtonTypeEnum from '../../../Enums/ButtonTypeEnum';
import ButtonColorsEnum from '../../../Enums/ButtonColorsEnum';
import IconPositionEnum from '../../../Enums/IconPositionEnum';
import CrudTypesEnum from '../../../Enums/CrudTypesEnum';
import TpEvaluatorItem from '../../../Domain/TpEvaluatorItem';
import TpEvaluatorService from '../../../Services/TpEvaluatorService';
import TpEvaluatorSearchParams from './TpEvaluatorSearchParams';
import AttachTpEvaluatorWindowDescription, { AttachTpEvaluatorParams } from './AttachTpEvaluatorWindowDescription';

interface LinkedTpEvaluatorsTabProps {
  /** The primary TpEvaluator whose linked profiles are managed here. */
  tpEvaluatorId: number;
  /** The row the detail popup was opened from (a TpEvaluatorItem) — used until the tab re-fetches. */
  initialItem?: any;
}

// CD-5527: "Linked TP Evaluators" tab of the TpEvaluator detail popup. The list page collapses children
// into their primary row, so this tab is the only place the linked profiles are visible and managed:
// a "Link TP Evaluator" action opens the attach dialog, and each child row's menu offers "Detach from
// primary" (TpEvaluatorItem hides it on rows without a PrimaryTpEvaluatorId). After a link or detach the
// tab re-fetches the primary (so the popup stays open and several profiles can be linked in a row) and
// tells the list page through TpEvaluatorItem.linkingChangedFunction, which refreshes the main list once
// the popup is closed.
export default function LinkedTpEvaluatorsTab({ tpEvaluatorId, initialItem = null }: LinkedTpEvaluatorsTabProps) {
  const [primary, setPrimary] = useState<any>(initialItem);
  const [loading, setLoading] = useState<boolean>(false);
  const [attaching, setAttaching] = useState<boolean>(false);

  const load = useCallback(() => {
    const searchItem: any = new (TpEvaluatorSearchParams as any)();
    searchItem.TpEvaluatorId = `${tpEvaluatorId}`;
    setLoading(true);
    return TpEvaluatorService.GetAllItemBySearching('TpEvaluatorId', 'Asc', 1, 1, searchItem, true)
      .then((result: any) => {
        const items = TpEvaluatorService.ConvertDBItems(result?.data ?? []);
        setPrimary(items.find((x: any) => `${x.TpEvaluatorId}` === `${tpEvaluatorId}`) ?? items[0] ?? null);
        setLoading(false);
      })
      .catch((error: any) => {
        setLoading(false);
        ErrorMessage.SetErrorMessage('LTET46', error);
      });
  }, [tpEvaluatorId]);

  const linkingChanged = () => {
    const domain = TpEvaluatorItem as any;
    if (domain.linkingChangedFunction) domain.linkingChangedFunction();
  };

  const detach = (child: any, reason: string) => {
    if (!reason || reason.trim() === '') {
      ErrorMessage.SetWarning('A reason is required to detach a TP Evaluator.');
      return;
    }
    ErrorMessage.SetLoading(true);
    TpEvaluatorService.DetachFromPrimary({
      TpEvaluatorId: child.TpEvaluatorId,
      PrimaryTpEvaluatorId: child.PrimaryTpEvaluatorId,
      Reason: reason.trim(),
    })
      .then((result: any) => {
        ErrorMessage.SetLoading(false);
        ErrorMessage.SetSuccess(typeof result === 'string' && result ? result : 'The TP Evaluator has been detached.');
        linkingChanged();
        load();
      })
      .catch((error: any) => {
        ErrorMessage.SetLoading(false);
        ErrorMessage.SetErrorMessage(error?.isBusinessError ? error.message : 'LTET72', error);
      });
  };

  // Bind the child rows' Detach action while this tab is mounted. Domain statics are the repo's row-action
  // convention (see TpEvaluatorAdmin / TpEvaluatorLinkAdmin); re-bound on every render on purpose.
  (TpEvaluatorItem as any).detachTpEvaluatorFunction = (child: any) => {
    ErrorMessage.SetWarningWithComment(
      `Detach TP Evaluator #${child.TpEvaluatorId} (${child.Name}) from primary TP Evaluator #${child.PrimaryTpEvaluatorId}? ` +
        'It becomes a separate profile again and its future payments are exported on their own.',
      'Reason:',
      (reason: string) => detach(child, reason),
      'Detach',
      'Cancel'
    );
  };

  useEffect(() => {
    if (!initialItem) {
      load();
    }
  }, [initialItem, load]);

  const onAttached = () => {
    setAttaching(false);
    linkingChanged();
    load();
  };

  if (!primary && loading) return <Loading />;
  if (!primary) return null;

  return (
    <div className='contentwrapper'>
      <FigmaButtonRow
        className='InnerMenuButtonFigmaList'
        buttons={
          [
            {
              iconName: 'fa-solid fa-link',
              label: 'Link TP Evaluator',
              type: ButtonTypeEnum.NormalIconLeft,
              color: ButtonColorsEnum.Primary,
              iconPosition: IconPositionEnum.Left,
              onClick: () => setAttaching(true),
              tooltip: 'Link a duplicate profile of the same shopper under this one',
              roleKey: 'Evaulator_ButtonRowButton_PrimaryLink',
            },
          ] as any
        }
      />
      {attaching && (
        <GeneralEditWizzardBase
          crudType={CrudTypesEnum.Create}
          editedItem={new AttachTpEvaluatorParams(primary)}
          clickOnCancel={() => setAttaching(false)}
          clickOnSave={onAttached}
          windowDescription={AttachTpEvaluatorWindowDescription}
          approveBeforeSaveCheck={() => true}
          approveBeforeSaveMessage={(edited: any) =>
            `Linking TP Evaluator #${edited.TpEvaluatorId} under #${edited.PrimaryTpEvaluatorId} re-routes its pending payments, ` +
            'payment exports and Tipalti payee to this profile. Continue?'
          }
          errorCode='LTET128'
        />
      )}
      {loading ? (
        <Loading />
      ) : (
        <DataTable
          itemList={primary.AttachedTpEvaluators ?? []}
          type={TpEvaluatorItem}
        />
      )}
    </div>
  );
}
